"use client";

import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Calendar } from "components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";

interface DatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: boolean;
}

export function DatePicker({
  value,
  onChange,
  placeholder = "MM/DD/YYYY",
  className,
  disabled,
  error,
}: DatePickerProps) {
  const [inputValue, setInputValue] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);

  // Initialize input value from prop (but not when user is actively typing)
  React.useEffect(() => {
    if (!isFocused) {
      if (value) {
        setInputValue(format(value, "MM/dd/yyyy"));
      } else {
        setInputValue("");
      }
    }
  }, [value, isFocused]);

  const parseDate = (dateString: string): Date | undefined => {
    if (!dateString.trim()) return undefined;

    // Try multiple date formats
    const formats = [
      "MM/dd/yyyy",
      "MM/dd/yy",
      "M/d/yyyy",
      "M/d/yy",
      "MM-dd-yyyy",
      "MM-dd-yy",
      "yyyy-MM-dd",
    ];

    for (const formatStr of formats) {
      try {
        const parsed = parse(dateString, formatStr, new Date());
        if (isValid(parsed)) {
          return parsed;
        }
      } catch {
        // Continue to next format
      }
    }

    return undefined;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    const parsedDate = parseDate(inputValue);

    if (parsedDate) {
      // Ensure the date is in local timezone to avoid day shifting
      const localDate = new Date(
        parsedDate.getFullYear(),
        parsedDate.getMonth(),
        parsedDate.getDate()
      );
      onChange?.(localDate);
      // Format the date properly after successful parse
      setInputValue(format(localDate, "MM/dd/yyyy"));
    } else if (inputValue.trim() === "") {
      onChange?.(undefined);
      setInputValue("");
    }
    // If invalid date, keep the input as is so user can correct it
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleInputBlur();
    }
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    if (date) {
      // Ensure the date is in local timezone to avoid day shifting
      const localDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );
      onChange?.(localDate);
      setInputValue(format(localDate, "MM/dd/yyyy"));
    } else {
      onChange?.(undefined);
      setInputValue("");
    }
    setIsOpen(false);
  };

  return (
    <div className={cn("relative", className)}>
      <Input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className="h-10 pr-10"
        aria-invalid={error}
      />
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-10 w-10 p-0 hover:bg-transparent"
            disabled={disabled}
            type="button"
          >
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={value}
            onSelect={handleCalendarSelect}
            defaultMonth={value || new Date()}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
